import {
  AccessField,
  Binary,
  Bool,
  CalendarValue,
  Call,
  Convert,
  type Expression,
  type ExpressionVisitor,
  Group,
  Identifier,
  Negate,
  Num,
  productMassFractionFromConvert,
  SimpleEvalError,
  Str,
} from './ast.ts';
import { dateConvertToCalendarRange, dateRangeConversionIsValid, UnitConversionError } from './units.ts';

export type ExpressionValidResultTypesSimple = Num | Str | Bool | CalendarValue;
export type ExpressionValidResultTypes =
  | ExpressionValidResultTypesSimple
  | SimpleEvalError
  | ((...args: ExpressionValidResultTypes[]) => ExpressionValidResultTypes);

export class SimpleExpressionEvaluator implements ExpressionVisitor<ExpressionValidResultTypes> {
  public readonly expression: Expression;
  public readonly variables: Map<string, ExpressionValidResultTypes>;

  constructor(expression: Expression, variables: Map<string, ExpressionValidResultTypes>) {
    this.expression = expression;
    this.variables = variables;
  }

  evaluate(): ExpressionValidResultTypes {
    return this.expression.accept(this);
  }

  visitNum(node: Num): Num {
    return node;
  }

  visitStr(node: Str): Str {
    return node;
  }

  visitBool(node: Bool): Bool {
    return node;
  }

  visitCalendarValue(node: CalendarValue): CalendarValue {
    return node;
  }

  visitIdentifier(node: Identifier): ExpressionValidResultTypes {
    const val = this.variables.get(node.nameWithAnnotation);
    if (!val) {
      return new SimpleEvalError(`Could not find identifier: ${node.nameWithAnnotation}`, {
        type: 'missingVariable',
        nameWithAnnotation: node.nameWithAnnotation,
      });
    }

    return val;
  }

  visitAccessField(node: AccessField): ExpressionValidResultTypes {
    const obj = node.expr.accept(this);
    if (obj instanceof SimpleEvalError) {
      return obj;
    }

    if (!obj || typeof obj !== 'object') {
      return new SimpleEvalError(`Could not access ${node.name.nameWithAnnotation} on: ${obj}`);
    }

    return (obj as any)[node.name.nameWithAnnotation];
  }

  visitNegate(node: Negate): Num | SimpleEvalError {
    const innerNode = node.expr.accept(this);
    if (innerNode instanceof SimpleEvalError) {
      return innerNode;
    }

    if (!(innerNode instanceof Num)) {
      return new SimpleEvalError(`Cannot negate non-number type: ${innerNode}`);
    }
    return innerNode.negate();
  }

  visitCall(node: Call): ExpressionValidResultTypes {
    if (node.callee instanceof Identifier) {
      const funcName = node.callee.nameWithAnnotation;
      return new SimpleEvalError(`Unexpected call to unknown function: ${funcName}`);
    } else {
      const func = node.callee.accept(this);
      if (func instanceof SimpleEvalError) {
        return func;
      }

      if (typeof func !== 'function') {
        return new SimpleEvalError(`Expected function, got ${func}`);
      }

      const args: ExpressionValidResultTypes[] = [];
      for (const arg of node.args) {
        const argVal = arg.accept(this);
        if (argVal instanceof SimpleEvalError) {
          return argVal;
        }

        args.push(argVal);
      }
      return func(...args);
    }
  }

  visitGroup(node: Group): ExpressionValidResultTypes {
    return node.expr.accept(this);
  }

  visitConvert(node: Convert): Num | CalendarValue | SimpleEvalError {
    const converting = node.expr.accept(this);
    if (converting instanceof SimpleEvalError) {
      return converting;
    }

    if (converting instanceof Num) {
      let source: Num = converting;
      const productMassFraction = productMassFractionFromConvert(node, this.variables);
      if (productMassFraction) {
        const massPerVolume = productMassFraction.massPerVolume();
        if (massPerVolume instanceof SimpleEvalError) {
          return massPerVolume;
        }
        source = massPerVolume;
      }

      const converted = source.units.convertTo(node.units, source.value);
      if (converted instanceof UnitConversionError) {
        if (source.units.isScalar() && node.units.isDensity()) {
          return new SimpleEvalError(
            `Cannot convert the unitless value ${source.toString()} to ${node.units.toString()}. Only a product's chemical or elemental concentration, used on its own, can be converted directly to a mass per volume.`,
          );
        }
        return new SimpleEvalError(converted.message);
      }

      return new Num(converted, node.units);
    } else if (converting instanceof CalendarValue) {
      const destinationType = node.units.toCalendarType();
      if (destinationType === null) {
        return new SimpleEvalError(
          `Cannot convert calendar value to: ${node.units.toString()} (${node.units.toDimensionString()})`,
        );
      }

      if (converting.value.calendarType === destinationType) {
        return converting;
      }

      const validity = dateRangeConversionIsValid(converting.value.calendarType, destinationType);
      if (!validity.valid) {
        return new SimpleEvalError(validity.explanation);
      }

      const converted = dateConvertToCalendarRange(converting.value.start, destinationType);
      if (converted instanceof UnitConversionError) {
        return new SimpleEvalError(converted.message);
      }
      return new CalendarValue(converted);
    } else {
      return new SimpleEvalError(`Cannot convert non-number non-date value: ${converting}`);
    }
  }

  visitBinary(node: Binary): Str | Num | Bool | SimpleEvalError {
    const left = node.left.accept(this);
    if (left instanceof SimpleEvalError) {
      return left;
    }

    const right = node.right.accept(this);
    if (right instanceof SimpleEvalError) {
      return right;
    }

    if (
      node.operator === '=' ||
      node.operator === '!=' ||
      node.operator === '<' ||
      node.operator === '>' ||
      node.operator === '<=' ||
      node.operator === '>='
    ) {
      if (
        (left instanceof Num && right instanceof Num) ||
        (left instanceof Str && right instanceof Str) ||
        (left instanceof Bool && right instanceof Bool) ||
        (left instanceof CalendarValue && right instanceof CalendarValue)
      ) {
        if (left instanceof Num && right instanceof Num) {
          if (!left.units.canAddOrSubtract(right.units)) {
            return new SimpleEvalError(`Cannot compare numbers of different units (${left.units} vs. ${right.units}).`);
          }
        }

        if (left instanceof CalendarValue && right instanceof CalendarValue) {
          if (left.value.calendarType !== right.value.calendarType) {
            return new SimpleEvalError(
              `Cannot compare calendar values of different types (${left.value.calendarType} vs. ${right.value.calendarType}).`,
            );
          }
        }

        const leftValue = left instanceof CalendarValue ? left.value.key : left.value;
        const rightValue = right instanceof CalendarValue ? right.value.key : right.value;

        if (node.operator === '=') {
          return Bool.from(leftValue === rightValue);
        } else if (node.operator === '!=') {
          return Bool.from(leftValue !== rightValue);
        } else if (node.operator === '<') {
          return Bool.from(leftValue < rightValue);
        } else if (node.operator === '>') {
          return Bool.from(leftValue > rightValue);
        } else if (node.operator === '<=') {
          return Bool.from(leftValue <= rightValue);
        } else if (node.operator === '>=') {
          return Bool.from(leftValue >= rightValue);
        } else {
          const unexpected: never = node.operator;
          return new SimpleEvalError(`Encountered unexpected binary operator: ${unexpected}`);
        }
      } else {
        return new SimpleEvalError(`Cannot compare mismatched types: ${left} ${node.operator} ${right}`);
      }
    }

    if (left instanceof Str || right instanceof Str) {
      if (node.operator === '+') {
        return new Str(String(left) + String(right));
      } else {
        return new SimpleEvalError(`Cannot use operator '${node.operator}' on strings`);
      }
    }

    if (!(left instanceof Num)) {
      return new SimpleEvalError(`Cannot operate on non-number type: ${left}`);
    }

    if (!(right instanceof Num)) {
      return new SimpleEvalError(`Cannot operate on non-number type: ${right}`);
    }

    if (node.operator === '+') {
      return left.add(right);
    } else if (node.operator === '-') {
      return left.subtract(right);
    } else if (node.operator === '*') {
      return left.multiply(right);
    } else if (node.operator === '/') {
      return left.divide(right);
    } else if (node.operator === '^') {
      return left.power(right);
    } else {
      const unexpected: never = node.operator;
      return new SimpleEvalError(`Encountered unexpected binary operator: ${unexpected}`);
    }
  }
}
